<template>
  <div class="page-content">
    <!-- conditional success message -->
    <div v-if="showSuccessMessage" class="success">
      <h3 v-translate>Password reset email sent</h3>
      <p v-translate>
        If the provided email address is associated with an account, an email with password reset
        instructions has been sent to it.
      </p>
      <p v-translate>(It may be in your spam or junk folder.)</p>
    </div>
    <!-- conditional form -->
    <div v-else class="form">
      <h3 v-translate>Reset password</h3>
      <!-- email field -->
      <div class="email-field">
        <md-field :class="{ 'md-invalid': this.errors.length }">
          <label v-translate>email</label>
          <md-input required v-model="email" name="email"></md-input>
        </md-field>
      </div>
      <!-- button -->
      <md-button
        class="md-dense md-raised button"
        @click="handleSubmit"
        :style="organizationCssVars"
      >
        <span v-if="!loading" key="next" v-translate>Get Reset Link</span>
        <span v-else-if="loading" key="spinner" disabled>
          <LoadSpinner :color="'white'" />
        </span>
      </md-button>
      <!-- conditional errors list -->
      <div id="errors" v-if="errors.length" class="md-errors">
        <ul>
          <li v-for="(error, index) in errors" :key="index">{{ error }}</li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import LoadSpinner from '../components/LoadSpinner'
import User from '@/services/users'
import { analyticsMixin } from '@/services/googleAnalytics'

export default {
  name: 'PasswordResetRequest',
  mixins: [analyticsMixin],
  components: {
    LoadSpinner,
  },
  data() {
    return {
      email: '',
      errors: [],
      loading: false,
      showSuccessMessage: false,
    }
  },
  methods: {
    handleSubmit() {
      if (this.loading) {
        return null // if this line is reached, then the user clicked the button before receiving response -- trying to double submit
      }
      this.loading = true
      User.api
        .requestPasswordReset(this.email)
        .then(() => {
          this.showSuccessMessage = true
          this.trackCustomPageview('PasswordResetEmailSent', '/password/reset/email-sent')
        })
        .catch(this.handleErrorResponse)
        .finally(() => {
          this.email = ''
          this.loading = false
        })
    },
    /**
     * @function    handleErrorResponse
     * Handles non-200 response from AJAX request to update user password.
     * Updates component state (this.errors) according to characteristics of incoming AJAX response (i.e. status-code)
     * @param   {Error}  error  - error object associated with the request
     */
    handleErrorResponse(error) {
      let { status } = error.response
      if (status >= 500) {
        // Check for 500-level status-code
        this.errors = ['Something went wrong! Please try again later.']
        return
      }
      // else: user inputs yielded failure of server-side validations
      this.errors = error.response.data.email // is an array of failed validations for email field
    },
  },
  computed: {
    ...mapState(['organizationCssVars']),
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/variables';
@import '@/styles/mixins/buttons';
@import '@/styles/mixins/containers';

.page-content {
  @include screen-container();
}

.form {
  width: 100%;
  display: flex;
  flex-flow: column;
  align-items: center;
}

.success {
  display: flex;
  flex-flow: column;
  align-items: center;

  p {
    text-align: center;
    margin-bottom: 1vh;
  }
}
.email-field {
  width: 15rem;
}

.button {
  @include primary-button(var(--dark-brand-color));
}

#errors {
  color: $ruby;
  font-weight: 600;
  font-size: 12px;
  display: flex;
  flex-flow: column;
  align-items: center;
}

ul {
  margin-top: 2vh;
  margin-bottom: 1vh;
}
</style>
